import { motion } from "framer-motion";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

/**
 * Framer Motion Variants
 *
 * These define a key/animations pair which is used to animate many components
 * using the same keys.
 */
const shadowVariants = {
  hover: Css.add("scale", 1.5).bgColor("rgba(246, 177, 78, 0.30)").$,
  active: Css.add("scale", 1.5).$,
};
const circleVariants = {
  hover: Css.bgBrightYellow.$,
  active: Css.bgBrightYellow.$,
};

type MapMarkerProps = {
  isActive?: boolean;
  isHovered?: boolean;
  onClick?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  testId?: string;
};
/**
 * Circular component which is used to represent a point of interest on the map.
 * This can be used as inspiration for a custom marker.
 *
 * When hovered, the marker will grow in size, change color of dot and shadow.
 * When active, the marker will grow in size and only change color of dot.
 */
export function MapMarker(props: MapMarkerProps) {
  const { isActive, isHovered, onClick, onHoverStart, onHoverEnd } = props;
  const tid = useTestIds(props, "mapMarker");

  // Conditionally render a `div` or `button` based on whether the marker is
  // interactive.
  const Wrapper = onClick ? motion.button : motion.div;

  return (
    // This is enabling the `hover` variant which all children with `variants`
    // will attempt to animate.
    <Wrapper
      {...tid}
      whileHover="hover"
      // Prioritize hover over active
      animate={isHovered ? "hover" : isActive ? "active" : undefined}
      onClick={() => onClick?.()}
      onHoverStart={() => onHoverStart?.()}
      onHoverEnd={() => onHoverEnd?.()}
    >
      {/* Shadow */}
      <motion.div
        css={Css.bgColor("rgba(53, 53, 53, .2)").br100.sqPx(20).df.aic.jcc.cursorPointer.$}
        variants={shadowVariants}
      >
        {/* White circle */}
        <div css={Css.bgWhite.br100.sqPx(16).df.aic.jcc.$}>
          {/* Inner coloured circle */}
          <motion.div css={Css.bgGray800.br100.sqPx(12).$} variants={circleVariants} />
        </div>
      </motion.div>
    </Wrapper>
  );
}
